import React from "react";
import { Program } from "../../services/api";
import {
  EyeIcon,
  EyeSlashIcon,
  PencilIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";

interface ProgramCardProps {
  program: Program;
  onEdit: (id: string) => void;
  onToggleVisibility: (id: string) => void;
  onViewDetails: (program: Program) => void;
}

const ProgramCard: React.FC<ProgramCardProps> = ({
  program,
  onEdit,
  onToggleVisibility,
  onViewDetails,
}) => {
  // Check if this is an AWIN-only program (missing admin data)
  const isIncomplete =
    !program.descriptionCustomer ||
    !program.conditions ||
    !program.cashbackRate ||
    !program.countries?.length;

  // Check if this is likely an AWIN-only program (has affiliateUrl but missing key admin fields)
  const isAwinOnly = program.affiliateUrl && isIncomplete;

  return (
    <div
      className={`bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow border cursor-pointer ${
        isAwinOnly
          ? "border-orange-200 dark:border-orange-700 bg-orange-50 dark:bg-orange-900/20"
          : "border-gray-200 dark:border-gray-700"
      }`}
      onClick={() => onViewDetails(program)}
    >
      <div className="flex items-center justify-between mb-4 gap-3">
        <div className="flex items-center space-x-3 min-w-0 flex-1">
          <div className="relative w-12 h-12">
            <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
              {program.logo ? (
                <img
                  src={program.logo}
                  alt={program.name}
                  className="w-12 h-12 rounded-lg object-cover"
                />
              ) : (
                <span className="text-gray-500 dark:text-gray-400 font-semibold text-lg">
                  {program.name.charAt(0).toUpperCase()}
                </span>
              )}
            </div>
            {program.priority && program.priority < 100 && (
              <span className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-xs font-bold bg-primary-600 text-white shadow-lg">
                {program.priority}
              </span>
            )}
          </div>
          <div>
            <div className="flex items-center space-x-2">
              <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
                {program.name}
              </h3>
              {isAwinOnly && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200">
                  <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
                  Needs Setup
                </span>
              )}
              {/* Display multiple categories if available, otherwise legacy single */}
              {program.categories && program.categories.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {program.categories.slice(0, 2).map((cat, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400"
                    >
                      {cat}
                    </span>
                  ))}
                  {program.categories.length > 2 && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                      +{program.categories.length - 2}
                    </span>
                  )}
                </div>
              ) : program.category ? (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400">
                  {typeof program.category === "object" &&
                  program.category !== null
                    ? (program.category as any).name ||
                      (program.category as any).categoryName ||
                      "Category"
                    : program.category}
                </span>
              ) : null}
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">
              {program.affiliateUrl}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2 flex-shrink-0">
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium ${
              program.visibility
                ? "bg-success-100 dark:bg-success-900 text-success-800 dark:text-success-200"
                : "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300"
            }`}
          >
            {program.visibility ? "Active" : "Hidden"}
          </span>
        </div>
      </div>

      <div className="space-y-2 mb-4">
        <div className="flex justify-between">
          <span className="text-sm text-gray-600 dark:text-gray-400">
            Cashback Rate:
          </span>
          {program.cashbackRate ? (
            <span className="font-semibold text-success-600 dark:text-success-400">
              {program.cashbackRate}%
            </span>
          ) : (
            <span className="text-sm text-orange-600 dark:text-orange-400 italic">
              Not set
            </span>
          )}
        </div>

        <div className="flex justify-between">
          <span className="text-sm text-gray-600 dark:text-gray-400">
            Countries:
          </span>
          {program.countries?.length ? (
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {program.countries.join(", ")}
            </span>
          ) : (
            <span className="text-sm text-orange-600 dark:text-orange-400 italic">
              Not set
            </span>
          )}
        </div>

        {program.descriptionCustomer ? (
          <div className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
            {program.descriptionCustomer}
          </div>
        ) : (
          <div className="text-sm text-orange-600 dark:text-orange-400 italic">
            Customer description not set
          </div>
        )}

        {program.conditions ? (
          <div className="text-sm text-gray-600 dark:text-gray-400 line-clamp-1">
            <span className="font-medium">Conditions:</span>{" "}
            {program.conditions}
          </div>
        ) : (
          <div className="text-sm text-orange-600 dark:text-orange-400 italic">
            Conditions not set
          </div>
        )}
      </div>

      <div className="flex justify-end space-x-2 pt-4 border-t border-gray-100 dark:border-gray-700">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleVisibility(program._id);
          }}
          className="flex items-center px-3 py-1 text-sm rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300"
          title={program.visibility ? "Hide program" : "Show program"}
        >
          {program.visibility ? (
            <EyeSlashIcon className="h-4 w-4 mr-1" />
          ) : (
            <EyeIcon className="h-4 w-4 mr-1" />
          )}
          {program.visibility ? "Hide" : "Show"}
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onEdit(program._id);
          }}
          className={`flex items-center px-3 py-1 text-sm rounded transition-colors ${
            isAwinOnly
              ? "bg-orange-600 text-white hover:bg-orange-700"
              : "bg-primary-600 text-white hover:bg-primary-700"
          }`}
        >
          <PencilIcon className="h-4 w-4 mr-1" />
          {isAwinOnly ? "Setup" : "Edit"}
        </button>
      </div>
    </div>
  );
};

export default ProgramCard;
